// src/components/AdminPortal.jsx

import React, { useState, useEffect } from 'react';
import {
    Settings, Plus, Trash2,
    Layers, X, GripVertical, RefreshCw,
    Lock, BookOpen, AlertCircle
} from 'lucide-react';
import { db, auth } from '../config/firebase-config';
import { 
    collection, doc, setDoc, onSnapshot, getDoc 
} from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { initialTopicContent } from '../data/content';
// --- IMPORT THE REFACTORED MANAGER ---
import ResourceManager from './admin/ResourceManager'; 

// The special libraries, listed ahead of the courses in the content picker.
const LIBRARY_DEFINITIONS = [
    { id: 'brain_health_resources', title: 'Brain Health Library', type: 'library' },
    { id: 'kidz_corner_resources', title: 'Kidz Corner', type: 'library' },
    { id: 'leadership_toolkit', title: 'Leadership Toolkit', type: 'library' }
];

const AdminPortal = ({ onExit, navigateTo }) => {
    // --- STATE ---
    // Unified list of ALL content types (Courses + Libraries)
    const [allContentOptions, setAllContentOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    
    // Org Context
    const [userOrgId, setUserOrgId] = useState(null);
    // VERTICAL PACKS (super-admin only): where course edits publish.
    //   'topics'           -> shared overlay, every org
    //   'topics_sport'     -> sport orgs only
    //   'topics_corporate' -> corporate orgs only
    const [packTarget, setPackTarget] = useState('topics');
    const PACK_OPTIONS = [
        { id: 'topics', label: 'Shared — every organization' },
        { id: 'topics_sport', label: 'Sport pack — sport orgs only' },
        { id: 'topics_corporate', label: 'Corporate pack — corporate orgs only' }
    ];
    const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);

    // Selection State
    const [selectedContentId, setSelectedContentId] = useState('');
    const [activeContentType, setActiveContentType] = useState('course'); // 'course' | 'library'

    // Form State (For Courses)
    const [newResource, setNewResource] = useState({ title: '', type: 'Video', url: '', content: '', tags: '' });
    const [newModuleTitle, setNewModuleTitle] = useState('');
    const [targetModuleId, setTargetModuleId] = useState('');

    // --- INITIALIZATION ---
    useEffect(() => {
        const initUser = async () => {
            const user = auth.currentUser;
            if (!user) return;

            const SUPER_EMAILS = ["codycuthill@easapp.ca"];
            if (SUPER_EMAILS.includes(user.email)) {
                setIsGlobalAdmin(true);
            } else {
                const userSnap = await getDoc(doc(db, "users", user.uid));
                if (userSnap.exists()) setUserOrgId(userSnap.data().organizationId);
            }
        };
        initUser();
    }, []);

    const handleExit = () => {
        if (onExit) onExit();
        else if (navigateTo) navigateTo('dashboard');
        else window.location.reload();
    };

    // Switch between Courses / Libraries and jump to the first item of that type
    const handleTypeChange = (type) => {
        setActiveContentType(type);
        const firstOfType = allContentOptions.find(i => type === 'library' ? i.type === 'library' : i.type !== 'library');
        setSelectedContentId(firstOfType ? firstOfType.id : '');
    };

    // --- 1. BUILD UNIFIED DROPDOWN LIST ---
    useEffect(() => {
        setLoading(true);

        // A. Start with Hardcoded Courses
        const hardcodedMap = new Map();
        Object.keys(initialTopicContent).forEach(key => {
            // Filter out 'libraries' that might be in initialContent to avoid dups if we handle them manually
            if (!['kidzcorner', 'leadership'].includes(key)) {
                hardcodedMap.set(key, { id: key, ...initialTopicContent[key], sourceType: 'course' });
            }
        });

        // Listeners
        let unsubGlobal = () => {};
        
        // --- FETCH GLOBAL TOPICS ---
        unsubGlobal = onSnapshot(collection(db, "topics"), (snap) => {
            const globalData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Merge Global Updates
            globalData.forEach(topic => {
                const existing = hardcodedMap.get(topic.id) || { modules: [] };
                hardcodedMap.set(topic.id, { ...existing, ...topic, sourceType: 'course' });
            });

            // Convert to Array
            const courses = Array.from(hardcodedMap.values())
                .filter(t => t.title && !['admin', 'dashboard', 'recovery'].includes(t.id))
                .sort((a, b) => a.title.localeCompare(b.title));

            // MERGE: Libraries + Courses
            const unifiedList = [
                ...LIBRARY_DEFINITIONS, // Put Special Libraries at top
                ...courses
            ];

            setAllContentOptions(unifiedList);
            setLoading(false);
            
            // Auto Select First
            if (unifiedList.length > 0 && selectedContentId === '') {
                setSelectedContentId(unifiedList[0].id);
            }
        });

        return () => unsubGlobal();
    }, [selectedContentId]);

    // --- 2. HANDLE SELECTION CHANGE ---
    useEffect(() => {
        const selectedItem = allContentOptions.find(i => i.id === selectedContentId);
        if (selectedItem) {
            setActiveContentType(selectedItem.type === 'library' ? 'library' : 'course');
        }
    }, [selectedContentId, allContentOptions]);


    // --- 3. [COURSE LOGIC ONLY] DEEP MERGE SELECTED TOPIC ---
    const [activeContent, setActiveContent] = useState({ modules: [] });

    useEffect(() => {
        if (!selectedContentId || activeContentType === 'library') return;

        const buildActiveContent = async () => {
            const hardcoded = initialTopicContent[selectedContentId] || { modules: [] };
            
            let globalDoc = {};
            try {
                const gSnap = await getDoc(doc(db, "topics", selectedContentId));
                if (gSnap.exists()) globalDoc = gSnap.data();
            } catch(e) {}

            // Vertical pack doc (super-admin editing a pack): its modules are the
            // editable set; shared base/global modules show locked for context.
            const editingPack = isGlobalAdmin && packTarget !== 'topics';
            let packDoc = {};
            if (editingPack) {
                try {
                    const kSnap = await getDoc(doc(db, packTarget, selectedContentId));
                    if (kSnap.exists()) packDoc = kSnap.data();
                } catch(e) {}
            }

            let privateDoc = {};
            if (userOrgId && !isGlobalAdmin) {
                try {
                    const pSnap = await getDoc(doc(db, "organizations", userOrgId, "private_topics", selectedContentId));
                    if (pSnap.exists()) privateDoc = pSnap.data();
                } catch(e) {}
            }

            const combinedModules = [];
            const globalModulesMap = new Map();
            if (globalDoc.managed && Array.isArray(globalDoc.modules)) {
                // CMS-managed topic: the saved list is the shared content (order and
                // deletions honoured) — same rule as TopicView / ResourceManager.
                globalDoc.modules.forEach(m => globalModulesMap.set(m.id, m));
            } else {
                (hardcoded.modules || []).forEach(m => globalModulesMap.set(m.id, m));
                (globalDoc.modules || []).forEach(m => globalModulesMap.set(m.id, m));
            }

            globalModulesMap.forEach(m => {
                combinedModules.push({ ...m, isLocked: !isGlobalAdmin || editingPack, source: 'global' });
            });

            if (editingPack && packDoc.modules) {
                packDoc.modules.forEach(m => {
                    combinedModules.push({ ...m, isLocked: false, source: 'pack' });
                });
            }

            if (privateDoc.modules) {
                privateDoc.modules.forEach(m => {
                    combinedModules.push({ ...m, isLocked: false, source: 'private' });
                });
            }

            setActiveContent({ ...hardcoded, ...globalDoc, ...packDoc, ...privateDoc, modules: combinedModules });
        };

        buildActiveContent();
    }, [selectedContentId, userOrgId, isGlobalAdmin, isSaving, activeContentType, packTarget]);

    // --- [COURSE LOGIC] HANDLERS ---
    const persistChanges = async (updatedModules) => {
        setIsSaving(true);
        try {
            const strip = ({ isLocked, source, ...rest }) => rest;
            const globalModsToSave = updatedModules.filter(m => m.source === 'global').map(strip);
            const packModsToSave = updatedModules.filter(m => m.source === 'pack').map(strip);
            const privateModsToSave = updatedModules.filter(m => m.source === 'private').map(strip);

            if (isGlobalAdmin && packTarget === 'topics') {
                const globalRef = doc(db, "topics", selectedContentId);
                // Saving the full list takes the topic over from content.js (managed)
                await setDoc(globalRef, { modules: globalModsToSave, managed: true, updatedAt: new Date().toISOString() }, { merge: true });
            }
            if (isGlobalAdmin && packTarget !== 'topics') {
                // Editing a vertical pack: only pack modules are writable here;
                // the shared base/global modules were shown locked.
                const packRef = doc(db, packTarget, selectedContentId);
                await setDoc(packRef, { modules: packModsToSave, updatedAt: new Date().toISOString() }, { merge: true });
            }

            if (userOrgId && !isGlobalAdmin) {
                const privateRef = doc(db, "organizations", userOrgId, "private_topics", selectedContentId);
                await setDoc(privateRef, { modules: privateModsToSave, updatedAt: new Date().toISOString() }, { merge: true });
            }
        } catch (error) {
            alert("Save Failed: " + error.message);
        }
        setIsSaving(false);
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const { source, destination, type } = result;
        
        if (type === 'MODULE') {
            const newModules = reorder(activeContent.modules, source.index, destination.index);
            setActiveContent({ ...activeContent, modules: newModules });
            persistChanges(newModules);
            return;
        }

        if (type === 'RESOURCE') {
            const moduleIndex = activeContent.modules.findIndex(m => m.id === source.droppableId);
            const targetModule = activeContent.modules[moduleIndex];

            if (targetModule.isLocked) {
                alert("You cannot reorder resources in a Global Module.");
                return;
            }
            
            const newResources = reorder(targetModule.resources || [], source.index, destination.index);
            const newModules = [...activeContent.modules];
            newModules[moduleIndex] = { ...targetModule, resources: newResources };
            
            setActiveContent({ ...activeContent, modules: newModules });
            persistChanges(newModules);
        }
    };

    const handleAddModule = async (e) => {
        e.preventDefault();
        if (!newModuleTitle || !selectedContentId) return;
        
        const newModule = { 
            id: `mod_${Date.now()}`, 
            title: newModuleTitle, 
            resources: [],
            source: isGlobalAdmin ? (packTarget !== 'topics' ? 'pack' : 'global') : 'private',
            isLocked: false
        };

        const newModules = [...activeContent.modules, newModule];
        setActiveContent({ ...activeContent, modules: newModules });
        await persistChanges(newModules);
        setNewModuleTitle('');
    };

    const handleAddResource = async (e) => {
        e.preventDefault();
        if (!newResource.title || !selectedContentId || !targetModuleId) return;

        const targetMod = activeContent.modules.find(m => m.id === targetModuleId);
        if (targetMod && targetMod.isLocked) {
            alert("You cannot add resources to a Global Module directly.");
            return;
        }

        const newModules = activeContent.modules.map(mod => {
            if (mod.id === targetModuleId) {
                return {
                    ...mod,
                    resources: [...(mod.resources || []), {
                        id: Date.now().toString(),
                        ...newResource,
                        tags: newResource.tags ? newResource.tags.split(',').map(t => t.trim()) : []
                    }]
                };
            }
            return mod;
        });

        setActiveContent({ ...activeContent, modules: newModules });
        await persistChanges(newModules);
        setNewResource({ title: '', type: 'Video', url: '', content: '', tags: '' });
    };

    const handleDeleteModule = async (moduleId) => {
        if (!window.confirm("Delete this module?")) return;
        const newModules = activeContent.modules.filter(m => m.id !== moduleId);
        setActiveContent({ ...activeContent, modules: newModules });
        await persistChanges(newModules);
    };

    const handleDeleteResource = async (moduleId, resourceId) => {
        if (!window.confirm("Delete this resource?")) return;
        const newModules = activeContent.modules.map(mod => {
            if (mod.id === moduleId) {
                return {
                    ...mod,
                    resources: mod.resources.filter(r => r.id !== resourceId)
                };
            }
            return mod;
        });
        setActiveContent({ ...activeContent, modules: newModules });
        await persistChanges(newModules);
    };

    // Auto-select target module for form
    useEffect(() => {
        if(activeContentType === 'course'){
            const firstEditable = activeContent.modules.find(m => !m.isLocked);
            if (firstEditable) setTargetModuleId(firstEditable.id);
            else setTargetModuleId('');
        }
    }, [activeContent.modules, activeContentType]);


    // --- RENDER ---
    if (loading) return <div className="p-12 text-center text-slate-500 animate-pulse flex flex-col items-center gap-4"><RefreshCw className="animate-spin" size={32}/> Loading Content Manager...</div>;

    return (
        <div className="w-full max-w-full px-6 pb-20 space-y-8">
            
            {/* Header */}
            <div className="bg-white p-6 rounded-2xl border border-brand-light shadow-sm flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-1 flex items-center gap-2">
                        <Settings className="text-brand-primary" /> Content Manager
                    </h1>
                    <p className="text-slate-500 text-sm">Manage Courses & Resource Libraries</p>
                </div>
                <button onClick={handleExit} className="px-4 py-2 bg-slate-100 hover:text-red-500 rounded-lg text-sm font-bold">Exit</button>
            </div>

            {/* SELECTOR */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 border-b pb-2 flex items-center gap-2">
                    <span className="bg-slate-100 text-slate-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
                    Choose what to manage
                </h2>

                {/* Type toggle (plain language, no "mode" jargon) */}
                <div className="inline-flex w-full md:w-auto gap-1 bg-slate-100 p-1 rounded-xl mb-4">
                    <button
                        type="button"
                        onClick={() => handleTypeChange('course')}
                        className={`flex-1 md:flex-none px-5 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${activeContentType !== 'library' ? 'bg-white text-brand-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <Layers size={16} /> Courses
                    </button>
                    <button
                        type="button"
                        onClick={() => handleTypeChange('library')}
                        className={`flex-1 md:flex-none px-5 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${activeContentType === 'library' ? 'bg-white text-brand-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <BookOpen size={16} /> Resource Libraries
                    </button>
                </div>

                {/* Item picker (only the chosen type) */}
                <label className="block text-xs font-bold text-slate-400 mb-1">
                    {activeContentType === 'library' ? 'Which library?' : 'Which course?'}
                </label>
                <select
                    className="w-full p-3 border border-slate-300 rounded-xl text-sm font-bold bg-slate-50 focus:ring-2 focus:ring-brand-primary outline-none"
                    value={selectedContentId}
                    onChange={(e) => setSelectedContentId(e.target.value)}
                >
                    {allContentOptions
                        .filter(i => activeContentType === 'library' ? i.type === 'library' : i.type !== 'library')
                        .map(t => (
                            <option key={t.id} value={t.id}>{t.title}</option>
                        ))}
                </select>

                {/* VERTICAL PACK TARGET — super-admin, courses only */}
                {isGlobalAdmin && activeContentType === 'course' && (
                    <div className="mt-3">
                        <label className="block text-xs font-bold text-slate-400 mb-1">Publish to</label>
                        <select
                            className={`w-full p-3 border rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-brand-primary ${packTarget === 'topics' ? 'bg-slate-50 border-slate-300' : 'bg-amber-50 border-amber-300 text-amber-900'}`}
                            value={packTarget}
                            onChange={(e) => setPackTarget(e.target.value)}
                        >
                            {PACK_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                        {packTarget !== 'topics' && (
                            <p className="text-[11px] text-amber-700 mt-1 font-medium">Pack mode: shared modules are shown locked for context; modules you add here only ever appear for {packTarget === 'topics_sport' ? 'sport' : 'corporate'} organizations.</p>
                        )}
                    </div>
                )}

                {/* Plain-language helper replaces the old "Mode" pill */}
                <div className="mt-3 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-3">
                    <AlertCircle size={14} className="text-slate-400 shrink-0 mt-0.5" />
                    <span>
                        {activeContentType === 'library'
                            ? 'Libraries are simple collections — drop in videos, links, or files and they appear for your group. No modules needed.'
                            : 'Courses are organized into modules. Create a module first, then add content into it — videos, links, PDFs, or templates.'}
                    </span>
                </div>
            </div>

            {/* --- CONDITIONAL RENDER BASED ON TYPE --- */}
            
            {activeContentType === 'library' ? (
                // === VIEW A: RESOURCE MANAGER (FOR LIBRARIES) ===
                <div className="animate-in fade-in slide-in-from-bottom-2">
                    <ResourceManager 
                        isRestricted={true} 
                        fixedOrgId={userOrgId}
                        // We FORCE the ResourceManager to look at the selected Library ID
                        forcedTargetCollection={selectedContentId} 
                    />
                </div>
            ) : (
                // === VIEW B: MODULE BUILDER (FOR COURSES) ===
                <div className="animate-in fade-in slide-in-from-bottom-2 space-y-6">
                    
                    {/* CREATE MODULE */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                        <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 border-b pb-2 flex items-center gap-2">
                            <span className="bg-slate-100 text-slate-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
                            Create New Module
                        </h2>
                        <form onSubmit={handleAddModule} className="flex gap-4 items-end">
                            <div className="flex-1">
                                <label className="block text-xs font-bold text-slate-400 mb-1">Module Title</label>
                                <input 
                                    type="text" 
                                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm"
                                    placeholder="e.g. Coach's Corner" 
                                    value={newModuleTitle} 
                                    onChange={(e) => setNewModuleTitle(e.target.value)} 
                                />
                            </div>
                            <button type="submit" className="px-6 py-2.5 bg-brand-dark hover:bg-slate-800 text-white rounded-lg font-medium text-sm flex items-center gap-2 h-fit">
                                <Plus size={16} /> Create
                            </button>
                        </form>
                    </div>

                    {/* ADD RESOURCE FORM */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                        <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 border-b pb-2 flex items-center gap-2">
                            <span className="bg-slate-100 text-slate-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">3</span>
                            Add Content to Course
                        </h2>
                        <form onSubmit={handleAddResource} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 mb-1">Target Module</label>
                                    <select 
                                        className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-slate-50 focus:ring-2 focus:ring-brand-primary outline-none"
                                        value={targetModuleId} 
                                        onChange={(e) => setTargetModuleId(e.target.value)}
                                    >
                                        <option value="">-- Select --</option>
                                        {activeContent.modules.filter(m => !m.isLocked).map(m => (
                                            <option key={m.id} value={m.id}>{m.title} (Editable)</option>
                                        ))}
                                    </select>
                                    {activeContent.modules.filter(m => !m.isLocked).length === 0 && (
                                        <p className="text-xs text-red-400 mt-1 font-bold">No editable modules. Create one below first.</p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 mb-1">Resource Title</label>
                                    <input 
                                        type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-primary outline-none"
                                        placeholder="e.g. Team Update Video" 
                                        value={newResource.title} 
                                        onChange={e => setNewResource({...newResource, title: e.target.value})} 
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {['Video', 'Link', 'PDF', 'Template'].map(type => (
                                    <button
                                        key={type} type="button" onClick={() => setNewResource({...newResource, type})}
                                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${newResource.type === type ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>

                            {newResource.type === 'Template' ? (
                                <textarea 
                                    className="w-full p-3 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-brand-primary outline-none h-32"
                                    placeholder="Paste template text here..."
                                    value={newResource.content} 
                                    onChange={e => setNewResource({...newResource, content: e.target.value})} 
                                />
                            ) : (
                                <input 
                                    type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm"
                                    placeholder={newResource.type === 'Video' ? "https://youtu.be/..." : "https://drive.google.com/..."}
                                    value={newResource.url} 
                                    onChange={e => setNewResource({...newResource, url: e.target.value})} 
                                />
                            )}

                            <button type="submit" disabled={!targetModuleId} className="w-full py-2.5 bg-brand-primary text-white rounded-lg font-bold hover:opacity-90 disabled:opacity-50">
                                <Plus size={18} /> Add Resource
                            </button>
                        </form>
                    </div>

                    {/* DRAG & DROP LIST */}
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <div>
                            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 pl-1 flex items-center gap-2">
                                <span className="bg-slate-100 text-slate-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">4</span>
                                Organize Content
                            </h2>
                            <Droppable droppableId="modules-list" type="MODULE">
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
                                        {activeContent.modules.map((module, index) => (
                                            <Draggable key={module.id} draggableId={module.id} index={index} isDragDisabled={module.isLocked}>
                                                {(provided) => (
                                                    <div 
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${module.isLocked ? 'border-slate-100 opacity-90' : 'border-slate-200'}`}
                                                    >
                                                        <div className={`p-4 border-b border-slate-100 flex items-center justify-between group ${module.isLocked ? 'bg-slate-50' : 'bg-white'}`}>
                                                            <div className="flex items-center gap-3">
                                                                <div {...provided.dragHandleProps} className={`cursor-grab active:cursor-grabbing ${module.isLocked ? 'pointer-events-none opacity-20' : 'text-slate-400'}`}>
                                                                    <GripVertical size={20} />
                                                                </div>
                                                                {module.isLocked ? <Lock size={18} className="text-slate-400"/> : <Layers size={18} className="text-brand-primary" />}
                                                                <div>
                                                                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                                                        {module.title}
                                                                        {module.isLocked && <span className="text-[10px] bg-slate-200 text-slate-500 px-2 rounded-full">Global</span>}
                                                                        {!module.isLocked && module.source === 'pack' && <span className="text-[10px] bg-amber-50 text-amber-700 px-2 rounded-full">{packTarget === 'topics_sport' ? 'Sport pack' : 'Corporate pack'}</span>}
                                                                        {!module.isLocked && module.source !== 'pack' && <span className="text-[10px] bg-blue-50 text-brand-primary px-2 rounded-full">{isGlobalAdmin ? 'Shared' : 'Private'}</span>}
                                                                    </h3>
                                                                </div>
                                                            </div>
                                                            {!module.isLocked && (
                                                                <button onClick={() => handleDeleteModule(module.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-2">
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            )}
                                                        </div>

                                                        <Droppable droppableId={module.id} type="RESOURCE" isDropDisabled={module.isLocked}>
                                                            {(provided) => (
                                                                <div ref={provided.innerRef} {...provided.droppableProps} className="p-4 space-y-2 min-h-[10px]">
                                                                    {module.resources && module.resources.map((res, resIndex) => (
                                                                        <Draggable key={res.id} draggableId={res.id} index={resIndex} isDragDisabled={module.isLocked}>
                                                                            {(provided) => (
                                                                                <div ref={provided.innerRef} {...provided.draggableProps} className={`flex items-center justify-between p-3 border rounded-xl bg-white ${module.isLocked ? 'border-slate-100 text-slate-400' : 'border-slate-100 hover:border-slate-300'}`}>
                                                                                    <div className="flex items-center gap-3 overflow-hidden">
                                                                                        <div {...provided.dragHandleProps} className={`cursor-grab active:cursor-grabbing ${module.isLocked ? 'pointer-events-none opacity-20' : 'text-slate-300'}`}>
                                                                                            <GripVertical size={16} />
                                                                                        </div>
                                                                                        <div className="min-w-0">
                                                                                            <p className="font-semibold text-sm truncate">{res.title}</p>
                                                                                        </div>
                                                                                    </div>
                                                                                    {!module.isLocked && (
                                                                                        <button onClick={() => handleDeleteResource(module.id, res.id)} className="text-slate-300 hover:text-red-500 p-2"><X size={16} /></button>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </Draggable>
                                                                    ))}
                                                                    {provided.placeholder}
                                                                </div>
                                                            )}
                                                        </Droppable>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    </DragDropContext>
                </div>
            )}
        </div>
    );
};

export default AdminPortal;